// src/context/OrderFlowContext.js
// ============================================
// GLOBAL STATE MANAGEMENT - Replaces URL-based state
// ============================================
import React, { createContext, useContext, useReducer, useCallback } from 'react';

const OrderFlowContext = createContext(null);

const STORAGE_KEY = 'resto_order_flow_state';

// Default Initial state
const defaultState = {
    orderType: 'delivery',       // 'delivery' | 'collection'
    isPreOrder: false,
    scheduledDate: null,
    scheduledTime: null,
    selectedStore: null,         // { id, name, location, week_schedule, ... }
    scheduleInfo: null,          // Pre-order schedule display info
};

const getInitialState = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Failed to parse order flow state from local storage', e);
    }
    return defaultState;
};

// Action types
const ACTIONS = {
    SET_ORDER_TYPE: 'SET_ORDER_TYPE',
    SET_SCHEDULE: 'SET_SCHEDULE',
    SET_STORE: 'SET_STORE',
    SET_PRE_ORDER: 'SET_PRE_ORDER',
    CLEAR_SCHEDULE: 'CLEAR_SCHEDULE',
    RESET: 'RESET',
};

// Reducer
function orderFlowReducer(state, action) {
    switch (action.type) {
        case ACTIONS.SET_ORDER_TYPE:
            return { ...state, orderType: action.payload };

        case ACTIONS.SET_SCHEDULE:
            return {
                ...state,
                isPreOrder: true,
                scheduledDate: action.payload.date,
                scheduledTime: action.payload.time,
                scheduleInfo: action.payload.scheduleInfo || null,
            };

        case ACTIONS.SET_STORE:
            return { ...state, selectedStore: action.payload };

        case ACTIONS.SET_PRE_ORDER:
            return { ...state, isPreOrder: action.payload };

        case ACTIONS.CLEAR_SCHEDULE:
            return {
                ...state,
                isPreOrder: false,
                scheduledDate: null,
                scheduledTime: null,
                scheduleInfo: null,
            };

        case ACTIONS.RESET:
            return { ...defaultState };

        default:
            return state;
    }
}

// Provider component
export function OrderFlowProvider({ children }) {
    const [state, dispatch] = useReducer(orderFlowReducer, defaultState, getInitialState);

    React.useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) {
            console.error('Failed to save order flow state to local storage', e);
        }
    }, [state]);

    const setOrderType = useCallback((type) => {
        dispatch({ type: ACTIONS.SET_ORDER_TYPE, payload: type });
    }, []);

    const setSchedule = useCallback((date, time, scheduleInfo = null) => {
        dispatch({
            type: ACTIONS.SET_SCHEDULE,
            payload: { date, time, scheduleInfo },
        });
    }, []);

    const setStore = useCallback((storeData) => {
        dispatch({ type: ACTIONS.SET_STORE, payload: storeData });
    }, []);

    const setPreOrder = useCallback((isPreOrder) => {
        dispatch({ type: ACTIONS.SET_PRE_ORDER, payload: isPreOrder });
    }, []);

    const clearSchedule = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_SCHEDULE });
    }, []);

    const reset = useCallback(() => {
        dispatch({ type: ACTIONS.RESET });
    }, []);

    const value = {
        // State
        ...state,
        // Actions
        setOrderType,
        setSchedule,
        setStore,
        setPreOrder,
        clearSchedule,
        reset,
    };

    return (
        <OrderFlowContext.Provider value={value}>
            {children}
        </OrderFlowContext.Provider>
    );
}

// Hook
export function useOrderFlow() {
    const context = useContext(OrderFlowContext);
    if (!context) {
        throw new Error('useOrderFlow must be used within an OrderFlowProvider');
    }
    return context;
}

export default OrderFlowContext;
